import { useState, useEffect } from 'react';
import { collection, query, getDocs, where, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import toast from 'react-hot-toast';

const AVAILABLE_COINS = [
  { value: 'FLCV', label: 'FLCV' },
  { value: 'PETCN', label: 'PETCN' },
  { value: 'FLCHU', label: 'FLCHU' },
  { value: 'TRLCN', label: 'TRLCN' },
  { value: 'SHPCN', label: 'SHPCN' },
  { value: 'VPVB', label: 'VPVB' },
  { value: 'GLDCN', label: 'GLDCN' },
  { value: 'CHRTC', label: 'CHRTC' },
  { value: 'HLTCN', label: 'HLTCN' }
];

const TRANSFER_TYPES = [
  { value: 'single', label: 'Egyéni utalás' },
  { value: 'bulk', label: 'Tömeges utalás' },
  { value: 'airdrop', label: 'Airdrop' }
];

export default function WalletTransferManager() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [transferType, setTransferType] = useState('single');
  const [selectedCoin, setSelectedCoin] = useState(AVAILABLE_COINS[0].value);
  const [amount, setAmount] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [csvData, setCsvData] = useState(null);

  // Felhasználó keresés
  const searchUsers = async (term) => {
    if (term.length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      const usersRef = collection(db, 'walletUsers');
      const q = query(
        usersRef,
        where('email', '>=', term),
        where('email', '<=', term + '\uf8ff')
      );
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Hiba a felhasználók keresése közben');
    }
  };

  // CSV fájl feldolgozása
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const rows = text.split('\n');
      const data = rows.map(row => {
        const [email, amount] = row.split(',');
        return { email: email?.trim(), amount: parseFloat(amount) || 0 };
      }).filter(row => row.email && row.amount > 0);

      setCsvData(data);
    };
    reader.readAsText(file);
  };

  // Utalás végrehajtása
  const handleTransfer = async () => {
    if (!selectedCoin || !amount || selectedRecipients.length === 0) {
      toast.error('Kérjük, töltse ki az összes mezőt');
      return;
    }

    setLoading(true);
    try {
      for (const recipient of selectedRecipients) {
        const transactionData = {
          transaction_id: `ADMIN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          amount: `${amount} ${selectedCoin}`,
          sender: {
            email: 'admin@system.com',
            user_id: 'SYSTEM'
          },
          recipient: {
            email: recipient.email,
            user_id: recipient.user_id
          },
          created_at: new Date(),
          status: 'completed',
          type: 'admin_transfer',
          transfer_type: transferType
        };

        // Tranzakció létrehozása
        await addDoc(collection(db, 'walletTransactions'), transactionData);

        // Egyenleg frissítése
        const userRef = doc(db, 'walletUsers', recipient.id);
        const currentBalance = parseFloat(recipient.details?.balances?.[selectedCoin] || 0);
        const newBalance = currentBalance + parseFloat(amount);

        await updateDoc(userRef, {
          [`details.balances.${selectedCoin}`]: newBalance.toString()
        });
      }

      toast.success('Utalás sikeresen végrehajtva');
      setSelectedRecipients([]);
      setAmount('');
    } catch (error) {
      console.error('Error executing transfer:', error);
      toast.error('Hiba történt az utalás során');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Wallet Utalások</h1>
        <p className="mt-1 text-sm text-gray-500">Utalások kezelése és végrehajtása</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {/* Utalás típusa */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Utalás típusa
          </label>
          <div className="grid grid-cols-3 gap-4">
            {TRANSFER_TYPES.map(type => (
              <button
                key={type.value}
                onClick={() => setTransferType(type.value)}
                className={`px-4 py-2 rounded-lg border ${
                  transferType === type.value
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Coin választó és összeg */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Coin
            </label>
            <select
              value={selectedCoin}
              onChange={(e) => setSelectedCoin(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            >
              {AVAILABLE_COINS.map(coin => (
                <option key={coin.value} value={coin.value}>
                  {coin.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Összeg
            </label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Címzettek kezelése */}
        {transferType === 'single' ? (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Címzett
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Keresés email alapján..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  searchUsers(e.target.value);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {searchResults.map(user => (
                    <button
                      key={user.id}
                      onClick={() => {
                        setSelectedRecipients([user]);
                        setSearchTerm('');
                        setSearchResults([]);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50"
                    >
                      <div className="text-sm font-medium">{user.email}</div>
                      <div className="text-xs text-gray-500">
                        Egyenleg: {user.details?.balances?.[selectedCoin] || 0} {selectedCoin}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CSV Feltöltés
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
              <div className="space-y-1 text-center">
                <Icons.Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500">
                    <span>CSV fájl feltöltése</span>
                    <input
                      type="file"
                      accept=".csv"
                      className="sr-only"
                      onChange={handleCsvUpload}
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500">
                  CSV formátum: email,összeg
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Kiválasztott címzettek */}
        {selectedRecipients.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Kiválasztott címzettek ({selectedRecipients.length})
            </h3>
            <div className="space-y-2">
              {selectedRecipients.map(recipient => (
                <div
                  key={recipient.id}
                  className="flex justify-between items-center p-2 bg-gray-50 rounded-lg"
                >
                  <div>
                    <div className="text-sm font-medium">{recipient.email}</div>
                    <div className="text-xs text-gray-500">
                      Egyenleg: {recipient.details?.balances?.[selectedCoin] || 0} {selectedCoin}
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedRecipients(prev => 
                      prev.filter(r => r.id !== recipient.id)
                    )}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <Icons.X className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Utalás gomb */}
        <div className="flex justify-end">
          <button
            onClick={handleTransfer}
            disabled={loading || !selectedCoin || !amount || selectedRecipients.length === 0}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? (
              <Icons.Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              'Utalás Végrehajtása'
            )}
          </button>
        </div>
      </div>
    </div>
  );
} 